import { useState, type FormEvent } from 'react';
import { useLocations } from '@/stores/locations';
import { useOrg } from '@/stores/identity';
import type { Location } from '@/lib/types';

const CANADIAN_TIMEZONES = [
  { value: '', label: '— inherit organization timezone —' },
  { value: 'America/Toronto', label: 'Eastern (Toronto)' },
  { value: 'America/Montreal', label: 'Eastern (Montreal)' },
  { value: 'America/Halifax', label: 'Atlantic (Halifax)' },
  { value: 'America/St_Johns', label: 'Newfoundland (St. John’s)' },
  { value: 'America/Winnipeg', label: 'Central (Winnipeg)' },
  { value: 'America/Regina', label: 'Central (Regina, no DST)' },
  { value: 'America/Edmonton', label: 'Mountain (Edmonton)' },
  { value: 'America/Vancouver', label: 'Pacific (Vancouver)' },
  { value: 'America/Whitehorse', label: 'Yukon (Whitehorse)' },
  { value: 'America/Yellowknife', label: 'Mountain (Yellowknife)' },
  { value: 'America/Iqaluit', label: 'Eastern (Iqaluit)' },
];

export interface LocationCreateFormProps {
  onCreated?: (loc: Location) => void;
  /**
   * Whether to show the prominent intro text. The onboarding flow shows it;
   * the in-app "+ New location" form hides it (the page itself supplies copy).
   */
  withIntro?: boolean;
  submitLabel?: string;
}

export function LocationCreateForm({
  onCreated,
  withIntro = false,
  submitLabel = 'Create location',
}: LocationCreateFormProps) {
  const org = useOrg();
  const add = useLocations((s) => s.add);

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('');
  const [splitTime, setSplitTime] = useState('12:00');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Location name is required.');
      return;
    }

    setSubmitting(true);
    try {
      const created = await add({
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
        timezone: timezone || null,
        shift_split_time: splitTime ? `${splitTime}:00` : '12:00:00',
      });
      onCreated?.(created);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {withIntro && (
        <p className="text-sm text-muted-foreground">
          You’re adding the first pharmacy under{' '}
          <strong>{org?.display_name ?? 'your organization'}</strong>. You can
          add more locations later from the Locations page.
        </p>
      )}

      <Field
        label="Pharmacy name"
        htmlFor="name"
        hint="Used everywhere this location appears in the app and on schedules."
      >
        <input
          id="name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Smith Pharmacy — Yonge St"
          className={inputClass}
        />
      </Field>

      <Field label="Address" htmlFor="address" hint="Free-text street address.">
        <input
          id="address"
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="e.g. 123 Yonge St, Toronto, ON M5C 1W4"
          className={inputClass}
        />
      </Field>

      <Field label="Phone" htmlFor="phone">
        <input
          id="phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(416) 555-0100"
          className={inputClass}
        />
      </Field>

      <Field
        label="Timezone"
        htmlFor="timezone"
        hint="Leave on inherit unless this location is in a different timezone than your organization."
      >
        <select
          id="timezone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className={inputClass}
        >
          {CANADIAN_TIMEZONES.map((tz) => (
            <option key={tz.value || 'inherit'} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </Field>

      <Field
        label="Shift split time"
        htmlFor="split-time"
        hint="The boundary between morning and afternoon shifts at this pharmacy. Used by half-day time-off requests. Default 12:00; common values 14:00 or 15:00."
      >
        <input
          id="split-time"
          type="time"
          value={splitTime}
          onChange={(e) => setSplitTime(e.target.value)}
          className={inputClass}
        />
      </Field>

      {error && (
        <p
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {submitting ? 'Creating...' : submitLabel}
      </button>

      <p className="text-xs text-muted-foreground">
        New locations start in <strong>draft</strong>. Set hours and any
        managers, then transition to <strong>active</strong>.
      </p>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';
